import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Chart as ChartJS,
    CategoryScale, LinearScale, PointElement, LineElement,
    Title, Tooltip, Filler, Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import Sidebar from './Sidebar';
import { api } from './api';
import { Server } from './types';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Filler, Legend);

/* ──────────────────────────── Types ──────────────────────────── */
interface LatestMetrics {
    cpu_percent: number;
    cpu_cores: number[];
    ram_total: number;
    ram_used: number;
    ram_percent: number;
    swap_percent: number;
    disks: { mount: string; device: string; total: number; used: number; percent: number }[];
    net_in: number;
    net_out: number;
    timestamp: string;
}

interface History {
    timestamps: string[];
    cpu: (number | null)[];
    ram: (number | null)[];
    net_in: (number | null)[];
    net_out: (number | null)[];
}

/* ──────────────────────────── Helpers ──────────────────────────── */
function fmtBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / k ** i).toFixed(1)} ${sizes[i]}`;
}

function fmtRate(bps: number): string {
    return `${fmtBytes(bps)}/s`;
}

function fmtTimestamp(iso: string): string {
    const d = new Date(iso + (iso.endsWith('Z') ? '' : 'Z'));
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function fmtLabel(iso: string): string {
    const d = new Date(iso + (iso.endsWith('Z') ? '' : 'Z'));
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

/* ──────────────────────────── Gauge (SVG ring) ──────────────────────────── */
function Gauge({ value, label, color }: { value: number; label: string; color: string }) {
    const r = 42;
    const circ = 2 * Math.PI * r;
    const pct  = Math.min(100, Math.max(0, value));
    const dash = (pct / 100) * circ;

    return (
        <div className="gauge-wrap">
            <svg viewBox="0 0 100 100" className="gauge-svg">
                {/* Track */}
                <circle cx="50" cy="50" r={r} fill="none" stroke="var(--border)" strokeWidth="10" />
                {/* Value */}
                <circle
                    cx="50" cy="50" r={r}
                    fill="none"
                    stroke={color}
                    strokeWidth="10"
                    strokeLinecap="round"
                    strokeDasharray={`${dash} ${circ}`}
                    strokeDashoffset={circ / 4}   /* start from top */
                    style={{ transition: 'stroke-dasharray 0.6s ease' }}
                />
                <text x="50" y="47" textAnchor="middle" className="gauge-pct">{Math.round(pct)}%</text>
                <text x="50" y="62" textAnchor="middle" className="gauge-label">{label}</text>
            </svg>
        </div>
    );
}

/* ──────────────────────────── Line Chart wrapper ──────────────────────────── */
const CHART_OPTIONS = (yLabel: string, yMax?: number) => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 300 } as const,
    plugins: {
        legend: { display: false },
        tooltip: {
            mode: 'index' as const,
            intersect: false,
            backgroundColor: 'rgba(17,24,39,0.95)',
            borderColor: '#1e293b',
            borderWidth: 1,
        },
    },
    scales: {
        x: {
            grid: { color: 'rgba(255,255,255,0.04)' },
            ticks: { color: '#64748b', maxTicksLimit: 6, maxRotation: 0 },
        },
        y: {
            min: 0,
            max: yMax,
            grid: { color: 'rgba(255,255,255,0.04)' },
            ticks: { color: '#64748b' },
            title: { display: true, text: yLabel, color: '#64748b', font: { size: 11 } },
        },
    },
});

function makeDataset(label: string, data: (number | null)[], color: string, fill = false) {
    return {
        label,
        data,
        borderColor: color,
        backgroundColor: fill ? color.replace(')', ', 0.12)').replace('rgb', 'rgba') : 'transparent',
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.3,
        fill,
    };
}

/* ──────────────────────────── Period selector ──────────────────────────── */
const PERIODS = ['1h', '6h', '24h', '7d'] as const;
type Period = typeof PERIODS[number];

function PeriodSelector({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
    return (
        <div className="period-selector">
            {PERIODS.map(p => (
                <button
                    key={p}
                    className={`period-btn ${value === p ? 'active' : ''}`}
                    onClick={() => onChange(p)}
                >{p}</button>
            ))}
        </div>
    );
}

/* ══════════════════════════════════════════════════════════════════ */
export default function ServerDetail() {
    const { id }   = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [server,  setServer]  = useState<Server | null>(null);
    const [latest,  setLatest]  = useState<LatestMetrics | null>(null);
    const [history, setHistory] = useState<History | null>(null);
    const [period,  setPeriod]  = useState<Period>('1h');
    const [loading, setLoading] = useState(true);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const loadLatest = useCallback(async () => {
        if (!id) return;
        const data = await api.get<LatestMetrics | null>(`/servers/${id}/metrics/latest`);
        setLatest(data);
    }, [id]);

    const loadHistory = useCallback(async (p: Period) => {
        if (!id) return;
        const data = await api.get<History>(`/servers/${id}/metrics/history?period=${p}`);
        setHistory(data);
    }, [id]);

    // Initial load
    useEffect(() => {
        if (!id) return;
        Promise.all([
            api.get<Server>(`/servers/${id}`).then(setServer),
            loadLatest(),
            loadHistory(period),
        ]).finally(() => setLoading(false));
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Refresh latest every 15s
    useEffect(() => {
        intervalRef.current = setInterval(() => {
            loadLatest();
            loadHistory(period);
        }, 15_000);
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [loadLatest, loadHistory, period]);

    // Period change
    const handlePeriod = (p: Period) => {
        setPeriod(p);
        loadHistory(p);
    };

    if (loading) return (
        <div className="app-layout">
            <Sidebar />
            <main className="main-content"><div className="loading-screen">Загрузка...</div></main>
        </div>
    );

    if (!server) return (
        <div className="app-layout">
            <Sidebar />
            <main className="main-content">
                <div className="empty-state">
                    <div className="empty-icon">❌</div>
                    <h2>Сервер не найден</h2>
                    <button className="btn-primary" style={{ maxWidth: 180 }} onClick={() => navigate('/servers')}>
                        ← Назад
                    </button>
                </div>
            </main>
        </div>
    );

    const labels = (history?.timestamps ?? []).map(fmtLabel);

    /* ── Нет данных от агента ── */
    const noAgent = latest === null;

    return (
        <div className="app-layout">
            <Sidebar />

            <main className="main-content">
                {/* ── Header ── */}
                <header className="page-header" style={{ marginBottom: 24 }}>
                    <div>
                        <button className="btn-back" onClick={() => navigate('/servers')}>
                            ← Серверы
                        </button>
                        <h1 style={{ marginTop: 8 }}>{server.name}</h1>
                        <div className="server-host" style={{ fontSize: 14, marginTop: 4 }}>
                            {server.host}
                            {latest && (
                                <span className="last-updated">
                                    &nbsp;· обновлено {fmtTimestamp(latest.timestamp)}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="header-actions">
                        <button
                            className="btn-secondary"
                            onClick={() => navigate(`/terminal/${id}`)}
                        >
                            💻 Терминал
                        </button>
                        <button
                            className="btn-secondary"
                            onClick={() => window.open(`/api/export/metrics/${id}?period=${period}`, '_blank')}
                        >
                            📥 CSV
                        </button>
                        <PeriodSelector value={period} onChange={handlePeriod} />
                    </div>               
                </header>

                {noAgent && (
                    <div className="agent-notice">
                        <span>📡</span>
                        <div>
                            <strong>Агент не подключён</strong>
                            <p>Установите агент на сервер <code>{server.host}</code>, чтобы видеть метрики CPU, RAM и дисков.</p>
                        </div>
                    </div>
                )}

                {/* ══ CPU ══ */}
                <div className="metric-section">
                    <h2 className="section-title">Процессор</h2>
                    <div className="metric-row">
                        <Gauge
                            value={latest?.cpu_percent ?? 0}
                            label="CPU"
                            color={gaugeColor(latest?.cpu_percent ?? 0)}
                        />
                        <div className="chart-area">
                            <Line
                                data={{
                                    labels,
                                    datasets: [makeDataset('CPU %', history?.cpu ?? [], 'rgb(99,102,241)', true)],
                                }}
                                options={CHART_OPTIONS('%', 100)}
                            />
                        </div>
                        {latest && latest.cpu_cores.length > 0 && (
                            <div className="cores-grid">
                                {latest.cpu_cores.map((v, i) => (
                                    <div key={i} className="core-bar-wrap">
                                        <div className="core-label">C{i}</div>
                                        <div className="core-track">
                                            <div
                                                className="core-fill"
                                                style={{
                                                    width: `${v}%`,
                                                    background: gaugeColor(v),
                                                }}
                                            />
                                        </div>
                                        <div className="core-pct">{Math.round(v)}%</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* ══ RAM ══ */}
                <div className="metric-section">
                    <h2 className="section-title">Память</h2>
                    <div className="metric-row">
                        <Gauge
                            value={latest?.ram_percent ?? 0}
                            label="RAM"
                            color={gaugeColor(latest?.ram_percent ?? 0)}
                        />
                        <div className="chart-area">
                            <Line
                                data={{
                                    labels,
                                    datasets: [makeDataset('RAM %', history?.ram ?? [], 'rgb(34,197,94)', true)],
                                }}
                                options={CHART_OPTIONS('%', 100)}
                            />
                        </div>
                        {latest && (
                            <div className="ram-stats">
                                <div className="ram-stat-row">
                                    <span>Всего</span>
                                    <span>{fmtBytes(latest.ram_total)}</span>
                                </div>
                                <div className="ram-stat-row">
                                    <span>Используется</span>
                                    <span className="txt-warn">{fmtBytes(latest.ram_used)}</span>
                                </div>
                                <div className="ram-stat-row">
                                    <span>Свободно</span>
                                    <span className="txt-ok">{fmtBytes(latest.ram_total - latest.ram_used)}</span>
                                </div>
                                <div className="ram-stat-row">
                                    <span>Swap</span>
                                    <span>{Math.round(latest.swap_percent)}%</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ══ Диски ══ */}
                {latest && latest.disks.length > 0 && (
                    <div className="metric-section">
                        <h2 className="section-title">Диски</h2>
                        <div className="disk-list">
                            {latest.disks.map((d, i) => (
                                <div key={i} className="disk-item">
                                    <div className="disk-header">
                                        <span className="disk-mount">{d.mount}</span>
                                        <span className="disk-size">
                                            {fmtBytes(d.used)} / {fmtBytes(d.total)}
                                        </span>
                                        <span
                                            className="disk-pct"
                                            style={{ color: gaugeColor(d.percent) }}
                                        >
                                            {Math.round(d.percent)}%
                                        </span>
                                    </div>
                                    <div className="disk-track">
                                        <div
                                            className="disk-fill"
                                            style={{
                                                width: `${d.percent}%`,
                                                background: gaugeColor(d.percent),
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ══ Сеть ══ */}
                <div className="metric-section">
                    <h2 className="section-title">Сеть</h2>
                    {latest && (
                        <div className="net-summary">
                            <div className="net-stat">
                                <span className="net-arrow net-in">↓</span>
                                <span className="net-val">{fmtRate(latest.net_in)}</span>
                                <span className="net-lbl">входящий</span>
                            </div>
                            <div className="net-stat">
                                <span className="net-arrow net-out">↑</span>
                                <span className="net-val">{fmtRate(latest.net_out)}</span>
                                <span className="net-lbl">исходящий</span>
                            </div>
                        </div>
                    )}
                    <div className="chart-area chart-area-full">
                        <Line
                            data={{
                                labels,
                                datasets: [
                                    makeDataset('Входящий', history?.net_in  ?? [], 'rgb(34,197,94)'),
                                    makeDataset('Исходящий', history?.net_out ?? [], 'rgb(251,146,60)'),
                                ],
                            }}
                            options={CHART_OPTIONS('Байт/с')}
                        />
                    </div>
                </div>
            </main>
        </div>
    );
}

/* Цвет по загрузке: зелёный → жёлтый → красный */
function gaugeColor(pct: number): string {
    if (pct < 60)  return 'rgb(34,197,94)';
    if (pct < 85)  return 'rgb(251,146,60)';
    return 'rgb(239,68,68)';
}
